import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import GenericButton from "widgets/controls/buttons/generic";
import { SearchInput } from "widgets/shared/inputs/search";
import Table from "widgets/controls/tables/genericTable";
import { CONST } from "models/constants";
import { deleteReticulation, reticulationPage } from "api/reticulation";
import { notifySimple } from "providers/toast";
import { useNotificationDialog } from "providers/notification-dialog-provider";
import { MdRefresh } from "react-icons/md";
import CircleButton from "widgets/controls/buttons/circle-button";
import ComboBox from "widgets/shared/inputs/combobox";
import defineReticulationTablecolumns, {
  ReticulationPageTableModel,
} from "models/tables/reticulationPage";
import { ReticulationTypeDropdown } from "models/dropdowns/reticulationTypes";
import ReticulationDialog from "widgets/dialogs/reticulation";
import { ReticulationFormType } from "widgets/forms/tools/reticulation";
import { setLoading } from "store/appSlice";
import SitesCombobox from "widgets/shared/inputs/usersCombobox";

type ReticulationFilters = {
  paginationState: { pageIndex: number; pageSize: number };
  sortingState: { id: string; desc: boolean }[];
  totCount: number;
  pageCount: number;
  search: string;
  siteId?: string;
  reticulationType?: string;
};

const Reticulation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notify } = useNotificationDialog();

  const [dataSource, setDataSource] = useState<ReticulationPageTableModel[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isAddReticulationOpen, setIsAddReticulationOpen] = useState(false);
  const [reticulationContext, setReticulationContext] = useState<
    "add" | "edit"
  >("add");
  const [reticulationValues, setReticulationValues] = useState<
    ReticulationFormType | undefined
  >(undefined);

  const [filters, setFilters] = useState<ReticulationFilters>({
    paginationState: { pageIndex: 0, pageSize: 10 },
    sortingState: [{ desc: true, id: "created" }],
    totCount: 0,
    pageCount: 0,
    search: "",
  });

  const columns = defineReticulationTablecolumns(
    (info) => {
      const row = info.row.original;
      navigate(`/admin/tools/reactflow/view?id=${row._id}`);
    },
    (info) => {
      const row = info.row.original;
      setReticulationContext("edit");
      setReticulationValues(row);
      setIsAddReticulationOpen(true);
    },
    (info) => {
      const row = info.row.original;
      navigate(`/admin/tools/reactflow?id=${row._id}`);
    },
    (info) => {
      const row = info.row.original;
      notify({
        header: t("notification.reticulation.delete.title"),
        message: t("notification.reticulation.delete.description"),
        okText: t("button.action.delete"),
        cancelText: t("button.action.cancel"),
        onOk: () => doDelete(row._id),
        onCancel: () => {},
      });
    }
  );

  const handleRowClick = (row: ReticulationPageTableModel) => {
    navigate(`/admin/tools/reactflow/view?id=${row._id}`);
  };

  const doDelete = async (id: string) => {
    dispatch(setLoading(true));
    const { status, isLoading } = await deleteReticulation(id);
    dispatch(setLoading(isLoading));
    setIsLoading(false);
    if (status === 200) {
      notifySimple(t("notification.reticulation.deleted"), "success");
      fetchData();
    } else {
      notifySimple(
        t(
          "notification.reticulation.delete.error",
          "Failed to delete reticulation"
        ),
        "error"
      );
    }
  };

  const fetchData = useCallback(async () => {
    const { paginationState, sortingState, search, siteId, reticulationType } =
      filters;
    setIsLoading(true);
    const { data, status, isLoading, error } = await reticulationPage({
      search: search,
      pageIndex: paginationState.pageIndex + 1,
      pageSize: paginationState.pageSize,
      sort: sortingState.length > 0 ? sortingState[0].id : "",
      desc: sortingState.length > 0 ? sortingState[0].desc : false,
      ...(siteId && { siteId }),
      ...(reticulationType && { reticulationType }),
    });
    setIsLoading(isLoading);
    if (status === 200 && !error) {
      setDataSource(data.data || []);
      setFilters((prevFilters) => ({
        ...prevFilters,
        paginationState: {
          pageIndex: data.pageIndex - 1,
          pageSize: paginationState.pageSize,
        },
        totCount: data.total,
        pageCount: Math.ceil(data.total / paginationState.pageSize),
      }));
    }
  }, [
    filters.search,
    filters.paginationState.pageIndex,
    filters.paginationState.pageSize,
    filters.sortingState,
    filters.siteId,
    filters.reticulationType,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClose = (refresh: boolean) => {
    setIsAddReticulationOpen(false);
    setReticulationContext("add");
    setReticulationValues(undefined);
    if (refresh) {
      fetchData();
    }
  };

  return (
    <div>
      <Table
        filterbar={
          <div className="flex flex-col gap-y-3">
            <div className="flex justify-end gap-2">
              <CircleButton
                onClick={fetchData}
                icon={MdRefresh}
                size="md"
                color="secondary"
                variant="ghost"
                tooltip={t("tooltip.refresh")}
                tooltipPlacement="bottom"
                disabled={isLoading}
              />
              <GenericButton
                onClick={() => {
                  setIsAddReticulationOpen(true);
                }}
                type="button"
                text={t("button.action.create.reticulation", "ADD")}
                size="md"
                permissionRole={CONST.ROLE_OWNER}
                disabled={isLoading}
              />
            </div>

            <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
              <div className="flex-1 lg:flex-grow-[1]">
                <SitesCombobox
                  id="site-filter"
                  value={filters.siteId}
                  onChange={(value) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      siteId: value,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  placeholder={t("common.select.site")}
                  sizes="md"
                  color="secondary"
                  rounded="rounded-xl"
                  variant="solid"
                  disabled={isLoading}
                  filter={true}
                />
              </div>
              <div className="flex-1 lg:flex-grow-[1]">
                <ComboBox
                  id="reticulation-type-filter"
                  items={ReticulationTypeDropdown}
                  value={filters.reticulationType}
                  onChange={(value) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      reticulationType: value,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  placeholder={t("filter.reticulation.type")}
                  sizes="md"
                  variant="solid"
                  color="secondary"
                  rounded="rounded-xl"
                  disabled={isLoading}
                  minWidth="200px"
                />
              </div>
              <div className="flex-1 lg:flex-grow-[1]">
                <SearchInput
                  onChange={(value) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      search: value,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  type="text"
                  value={filters.search}
                  sizes="md"
                  rounded="rounded-xl"
                  color="secondary"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        }
        loading={isLoading}
        data={dataSource}
        columns={columns}
        pagination={filters.paginationState}
        paginationOptions={{
          onPaginationChange: (pagination) => {
            const page =
              typeof pagination === "function"
                ? pagination(filters.paginationState)
                : pagination;
            setFilters((prevFilters) => ({
              ...prevFilters,
              paginationState: page,
            }));
          },
          rowCount: filters.totCount,
          pageCount: filters.pageCount,
        }}
        sorting={filters.sortingState}
        onSortingChange={(updaterOrValue) => {
          const newSortingState =
            typeof updaterOrValue === "function"
              ? updaterOrValue(filters.sortingState)
              : updaterOrValue;
          if (newSortingState && newSortingState.length > 0) {
            setFilters((prevFilters) => ({
              ...prevFilters,
              sortingState: newSortingState,
            }));
          } else {
            setFilters((prevFilters) => ({
              ...prevFilters,
              sortingState: [{ id: "created", desc: true }],
            }));
          }
        }}
        onRowClick={handleRowClick}
      />

      {isAddReticulationOpen && (
        <ReticulationDialog
          isOpen={isAddReticulationOpen}
          onClose={handleClose}
          context={reticulationContext}
          values={reticulationValues}
        />
      )}
    </div>
  );
};

export default Reticulation;
